import useOperatorLocale from './useOperatorLocale';

/**
 * useMinChargeFloor
 *
 * Returns the platform minimum-charge floor for the current operator's
 * currency. Mirrors the backend `services.currencies.min_charge_floor()`
 * (2× Stripe's per-currency minimum, so USD $1.00 / AED 4.00 / EUR €1.00 /
 * GBP £0.60 / JPY ¥100).
 *
 *   const { amount, currency, formatted, label } = useMinChargeFloor();
 *
 *   - amount: number (in major units)
 *   - currency: e.g. 'USD', 'AED'
 *   - formatted: '$1.00' or '4.00 AED'   (display-ready string)
 *   - label: 'Minimum $1.00' / 'Minimum 4.00 AED' (helper text)
 *   - isBelow(value): true if value < amount
 *
 * Phase C/D — use everywhere a customer or operator types a charge amount.
 */
const FLOORS = {
  USD: 1,
  AED: 4,
  GBP: 0.6,
  EUR: 1,
  JPY: 100,
  MXN: 20,
  CAD: 1,
  AUD: 1,
};

const useMinChargeFloor = () => {
  const { currencyCode } = useOperatorLocale();
  const code = (currencyCode || 'USD').toUpperCase();
  const amount = FLOORS[code] ?? 1;

  // AED is displayed as text label (no symbol convention in UAE)
  const formatted = code === 'AED'
    ? `${amount.toFixed(2)} AED`
    : `${code === 'GBP' ? '£' : code === 'EUR' ? '€' : code === 'JPY' ? '¥' : '$'}${amount.toFixed(code === 'JPY' ? 0 : 2)}`;

  return {
    amount,
    currency: code,
    formatted,
    label: `Minimum ${formatted}`,
    isBelow: (v) => Number(v) > 0 && Number(v) < amount,
  };
};

export default useMinChargeFloor;
